import { useEffect, useRef, useState, type FormEvent } from 'react';
import {
  ApiError,
  commitLog,
  interviewNext,
  listLogs,
  structureLog,
  type LogPhase,
  type SenpaiLog,
} from '../lib/api.js';
import { useFlywheel } from '../lib/flywheel.js';
import { VoiceInputButton } from './VoiceInputButton.js';
import { PhotoAnonymizer } from './PhotoAnonymizer.js';

/**
 * Alumni surface — "旅のログ" (trip log).
 *
 * Senpai use the app throughout their journey: pre-trip plans, during-trip
 * notes, post-trip reflections (Decision 19). This screen shows the
 * timeline of an alumnus's existing entries and lets them append a new
 * one via an interview-style back-and-forth → LLM-structured draft →
 * confirm → commit.
 *
 * The compose form used to render three textareas at once. Post-demo
 * feedback was that the blank-textarea pattern is hard for first-time
 * writers, so we now ask one question at a time and the next question
 * is generated against the prior answers (POST /log/interview/next).
 *
 * The component takes `senpaiId` + `displayName` so the user-list can
 * route any post-visit user into their own log. Demo wires Takumi here,
 * but other personas (Haruka/Souta/Mio) work the same way.
 */

interface AlumniProps {
  senpaiId: string;
  displayName: string;
}

const PHASE_LABELS: Record<LogPhase, string> = {
  before: '出発前',
  during: '滞在中',
  after: '帰国後',
};

const PHASE_HINTS: Record<LogPhase, string> = {
  before: '出発前 — 期待や不安、準備、計画など',
  during: '滞在中 — その日のワーク、出会い、その場の感情',
  after: '帰国後 — 振り返り、変わったこと、続けていること',
};

interface QA {
  question: string;
  answer: string;
}

export function Alumni({ senpaiId, displayName }: AlumniProps) {
  const [logs, setLogs] = useState<SenpaiLog[] | null>(null);
  const [error, setError] = useState<string | null>(null);
  const { emit } = useFlywheel();

  // Compose state
  const [phase, setPhase] = useState<LogPhase>('after');
  const [dayLabel, setDayLabel] = useState('');

  // Interview state
  const [qa, setQa] = useState<QA[]>([]);
  const [currentQuestion, setCurrentQuestion] = useState<string | null>(null);
  const [currentAnswer, setCurrentAnswer] = useState('');
  const [interviewDone, setInterviewDone] = useState(false);

  // Workflow state: idle → structuring → previewing → committing
  const [draft, setDraft] = useState<SenpaiLog | null>(null);
  const [busy, setBusy] = useState<'fetching' | 'structuring' | 'committing' | null>(null);

  // Track the last (senpai, phase) we fetched for so a phase toggle
  // refetches the opener but typing into the textarea doesn't.
  const fetchedKey = useRef<string | null>(null);

  useEffect(() => {
    let cancelled = false;
    setLogs(null);
    setError(null);
    listLogs(senpaiId)
      .then((r) => {
        if (!cancelled) setLogs(r.logs);
      })
      .catch((err) => {
        if (cancelled) return;
        setError(err instanceof ApiError ? err.userMessage : String(err));
      });
    return () => {
      cancelled = true;
    };
  }, [senpaiId]);

  // Fetch the opener question whenever (senpai, phase) changes — but only
  // if the user hasn't already answered something for the current
  // interview. We don't want a phase toggle mid-interview to wipe state
  // silently; we reset if needed and refetch.
  useEffect(() => {
    if (draft) return; // don't fetch while previewing draft
    const key = `${senpaiId}::${phase}`;
    if (fetchedKey.current === key && currentQuestion !== null) return;
    fetchedKey.current = key;

    let cancelled = false;
    setBusy('fetching');
    setError(null);
    setQa([]);
    setCurrentAnswer('');
    setInterviewDone(false);
    interviewNext({ senpai_id: senpaiId, phase, prior_qa: [] })
      .then((r) => {
        if (cancelled) return;
        setCurrentQuestion(r.question);
        setInterviewDone(r.done);
      })
      .catch((err) => {
        if (cancelled) return;
        setError(err instanceof ApiError ? err.userMessage : String(err));
        setCurrentQuestion(null);
      })
      .finally(() => {
        if (!cancelled) setBusy(null);
      });
    return () => {
      cancelled = true;
    };
    // currentQuestion intentionally NOT in deps — we only want this effect
    // to drive the initial fetch, not to refetch when we set the question
    // ourselves below. eslint will flag this; that's correct here.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [senpaiId, phase, draft]);

  const collectedQa = (): QA[] => {
    const trimmed = currentAnswer.trim();
    if (!currentQuestion || !trimmed) return qa;
    return [...qa, { question: currentQuestion, answer: trimmed }];
  };

  const onNextQuestion = async () => {
    const trimmed = currentAnswer.trim();
    if (!currentQuestion || !trimmed || busy) return;

    const newQa: QA[] = [...qa, { question: currentQuestion, answer: trimmed }];
    setQa(newQa);
    setCurrentAnswer('');
    setBusy('fetching');
    setError(null);
    try {
      const r = await interviewNext({
        senpai_id: senpaiId,
        phase,
        prior_qa: newQa,
        ...(dayLabel.trim() ? { day_label: dayLabel.trim() } : {}),
      });
      setCurrentQuestion(r.question);
      setInterviewDone(r.done);
    } catch (err) {
      setError(err instanceof ApiError ? err.userMessage : String(err));
    } finally {
      setBusy(null);
    }
  };

  const onStructure = async (e?: FormEvent) => {
    e?.preventDefault();
    const prompts = collectedQa();
    if (prompts.length === 0 || busy) return;
    setBusy('structuring');
    setError(null);
    try {
      const r = await structureLog({
        senpai_id: senpaiId,
        prompts,
        phase,
        ...(dayLabel.trim() ? { day_label: dayLabel.trim() } : {}),
      });
      setDraft(r.draft);
    } catch (err) {
      setError(err instanceof ApiError ? err.userMessage : String(err));
    } finally {
      setBusy(null);
    }
  };

  const onCommit = async () => {
    if (!draft) return;
    setBusy('committing');
    setError(null);
    try {
      await commitLog(draft);
      // Optimistically prepend the new entry to the timeline
      setLogs((prev) => (prev ? [...prev, draft] : [draft]));
      // Alumni → candidate: the new entry becomes part of the corpus
      // future candidates will pull from in /chat.
      emit({
        kind: 'SENPAI_LOG_APPENDED',
        actor: 'alumni',
        flowsTo: 'candidate',
        note: '振り返りが先輩のログに追加された',
      });
      // Reset compose state — the phase-driven effect will refetch the
      // opener for the next entry.
      setDraft(null);
      setQa([]);
      setCurrentAnswer('');
      setCurrentQuestion(null);
      setInterviewDone(false);
      setDayLabel('');
      fetchedKey.current = null;
    } catch (err) {
      setError(err instanceof ApiError ? err.userMessage : String(err));
    } finally {
      setBusy(null);
    }
  };

  const onDiscardDraft = () => {
    setDraft(null);
  };

  const onRestartInterview = () => {
    if (busy) return;
    setQa([]);
    setCurrentAnswer('');
    setCurrentQuestion(null);
    setInterviewDone(false);
    fetchedKey.current = null;
  };

  // Group logs by phase for the timeline
  const grouped: Record<LogPhase, SenpaiLog[]> = { before: [], during: [], after: [] };
  for (const l of logs ?? []) {
    if (l.phase in grouped) grouped[l.phase].push(l);
  }

  const canStructure =
    !busy && collectedQa().length > 0 && draft === null;
  const canNext =
    !busy && currentQuestion !== null && currentAnswer.trim().length > 0 && !interviewDone;

  return (
    <div className="main">
      <div className="container container-wide">
        <h1>{displayName}さんの旅のログ</h1>
        <p className="lede">
          出発前から、滞在中、帰国後。同じノートに書き継いだ記録です。
          新しい記録を加えると、未来の候補者の対話の手がかりになります。
        </p>

        {error && <div className="error">{error}</div>}

        {/* ── Timeline ─────────────────────────────────────────────────── */}
        {logs === null && !error && (
          <div className="muted center loading-block">読み込んでいます...</div>
        )}

        {logs && logs.length === 0 && (
          <div className="muted">まだ記録がありません。最初の1件を加えましょう。</div>
        )}

        {logs && logs.length > 0 && (
          <div className="timeline">
            {(['before', 'during', 'after'] as LogPhase[]).map((p) => {
              const entries = grouped[p];
              if (entries.length === 0) return null;
              return (
                <section key={p} className="timeline-section">
                  <h2 className="timeline-heading">{PHASE_LABELS[p]}</h2>
                  <div className="timeline-list">
                    {entries.map((entry) => (
                      <article key={entry.id} className="entry-card">
                        <header className="entry-header">
                          {entry.day_label && (
                            <span className="entry-day-label">{entry.day_label}</span>
                          )}
                          {entry.date_approx && (
                            <span className="entry-date">{entry.date_approx}</span>
                          )}
                        </header>
                        {entry.takeaways && entry.takeaways.length > 0 && (
                          <ul className="entry-takeaways">
                            {entry.takeaways.map((t, i) => (
                              <li key={i}>{t}</li>
                            ))}
                          </ul>
                        )}
                        <p className="entry-body">
                          {entry.body_markdown.length > 240
                            ? entry.body_markdown.slice(0, 240) + '…'
                            : entry.body_markdown}
                        </p>
                        {entry.tags && entry.tags.length > 0 && (
                          <div className="entry-tags">
                            {entry.tags.map((tag) => (
                              <span key={tag} className="citation-chip entry-tag">
                                {tag}
                              </span>
                            ))}
                          </div>
                        )}
                      </article>
                    ))}
                  </div>
                </section>
              );
            })}
          </div>
        )}

        {/* ── Compose / Interview ──────────────────────────────────────── */}
        {!draft && (
          <section className="compose-card">
            <h2>新しい記録を加える</h2>
            <p className="muted">
              質問に答える形で進みます。1問ずつ、思いついたまま答えてください。
              文章は最後に AI が整え、確認のうえで保存します。
            </p>
            <form onSubmit={onStructure}>
              <div className="field">
                <label className="field-label">フェーズ</label>
                <div className="phase-row" role="radiogroup">
                  {(['before', 'during', 'after'] as LogPhase[]).map((p) => (
                    <label key={p} className={phase === p ? 'selected' : ''}>
                      <input
                        type="radio"
                        name="phase"
                        value={p}
                        checked={phase === p}
                        onChange={() => setPhase(p)}
                        disabled={busy !== null && busy !== 'fetching'}
                      />
                      <div>
                        <div className="phase-pill">{PHASE_LABELS[p]}</div>
                        <div className="phase-hint">{PHASE_HINTS[p]}</div>
                      </div>
                    </label>
                  ))}
                </div>
              </div>

              <div className="field">
                <label className="field-label" htmlFor="day_label">
                  ラベル(任意)
                </label>
                <input
                  id="day_label"
                  className="input"
                  value={dayLabel}
                  onChange={(e) => setDayLabel(e.target.value)}
                  placeholder="例: Day 1 午前 / 帰国2週間後 / 出発前夜"
                  disabled={busy === 'structuring' || busy === 'committing'}
                />
              </div>

              {/* Prior Q&A transcript */}
              {qa.length > 0 && (
                <div className="interview-transcript">
                  {qa.map((p, i) => (
                    <div key={i} className="interview-turn">
                      <div className="interview-q">Q{i + 1}. {p.question}</div>
                      <div className="interview-a">{p.answer}</div>
                    </div>
                  ))}
                </div>
              )}

              {/* Current question */}
              {currentQuestion && (
                <div className="field">
                  <label className="field-label" htmlFor="current_answer">
                    Q{qa.length + 1}. {currentQuestion}
                  </label>
                  <textarea
                    id="current_answer"
                    className="textarea"
                    value={currentAnswer}
                    onChange={(e) => setCurrentAnswer(e.target.value)}
                    placeholder="思いついたまま書いて大丈夫です。マイクから話しかけても OK。"
                    disabled={busy === 'structuring' || busy === 'committing'}
                    autoFocus
                  />
                  <div className="field-trailing">
                    <VoiceInputButton
                      disabled={busy === 'structuring' || busy === 'committing'}
                      onTranscript={(text) =>
                        setCurrentAnswer((prev) =>
                          prev.trim() ? `${prev.trim()}\n${text.trim()}` : text.trim(),
                        )
                      }
                      onError={(message) => setError(message)}
                    />
                    <p className="muted voice-hint">
                      マイクで話すと、文字起こしされて回答欄に追加されます。送信前に編集できます。
                    </p>
                  </div>
                </div>
              )}

              {!currentQuestion && busy === 'fetching' && (
                <div className="muted center loading-block">
                  最初の質問を準備しています...
                </div>
              )}

              {interviewDone && currentQuestion && (
                <p className="muted">
                  これくらい話せていれば、ここで「整えてプレビュー」に進んで大丈夫です。続けて答えても構いません。
                </p>
              )}

              <div className="row row-mt-sm">
                {qa.length > 0 && (
                  <button
                    type="button"
                    className="btn-ghost"
                    onClick={onRestartInterview}
                    disabled={busy !== null}
                  >
                    最初からやり直す
                  </button>
                )}
                <div className="spacer" />
                <button
                  type="button"
                  className="btn-ghost"
                  onClick={onNextQuestion}
                  disabled={!canNext}
                >
                  {busy === 'fetching' && qa.length > 0 ? '次の質問を考えています...' : '次の質問へ'}
                </button>
                <button type="submit" className="btn" disabled={!canStructure}>
                  {busy === 'structuring' ? '整えています...' : 'ここまでで整える'}
                </button>
              </div>
            </form>
          </section>
        )}

        {draft && (
          <section className="preview-card">
            <h2>プレビュー</h2>
            <p className="muted">この内容で記録に加えます。修正したい場合は「書き直す」を押してください。</p>

            <div className="preview-row">
              <strong>フェーズ:</strong> {PHASE_LABELS[draft.phase]}
              {draft.day_label && (
                <>
                  {' / '}
                  <strong>ラベル:</strong> {draft.day_label}
                </>
              )}
            </div>

            {draft.takeaways && draft.takeaways.length > 0 && (
              <div className="preview-row">
                <strong>気づき:</strong>
                <ul className="preview-takeaway-list">
                  {draft.takeaways.map((t, i) => (
                    <li key={i}>{t}</li>
                  ))}
                </ul>
              </div>
            )}

            <div className="preview-row">
              <strong>本文:</strong>
              <div className="preview-body">{draft.body_markdown}</div>
            </div>

            {draft.tags && draft.tags.length > 0 && (
              <div className="preview-tags">
                {draft.tags.map((tag) => (
                  <span key={tag} className="citation-chip">
                    {tag}
                  </span>
                ))}
              </div>
            )}

            <div className="row">
              <button
                type="button"
                className="btn-ghost"
                onClick={onDiscardDraft}
                disabled={busy !== null}
              >
                書き直す
              </button>
              <div className="spacer" />
              <button
                type="button"
                className="btn"
                onClick={onCommit}
                disabled={busy !== null}
              >
                {busy === 'committing' ? '保存中...' : '記録に加える'}
              </button>
            </div>
          </section>
        )}

        {/* ── Photo anonymizer ─────────────────────────────────────────── */}
        <PhotoAnonymizer />
      </div>
    </div>
  );
}
